import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import { neutral, primary } from "app/themes/colors";

import {
  SCREENSHOT_BACKGROUNDS,
  SCREENSHOT_PADDINGS,
  type ScreenshotBackground,
  type ScreenshotBackgroundId,
  type ScreenshotPaddingId,
  findBackground
} from "./screenshot.types";

/**
 * What a swatch shows for each backdrop kind. The gradient and solid swatches
 * are the real fill, so the row is a preview rather than a legend; `blur` and
 * `none` have nothing literal to show and get a stand-in.
 */
const swatchFill = (background: ScreenshotBackground): SxProps => {
  if (background.kind === "gradient") {
    return {
      background: `linear-gradient(135deg, ${background.colors[0]}, ${background.colors[1]})`
    };
  }
  if (background.kind === "solid") {
    return { backgroundColor: background.colors[0] };
  }
  if (background.kind === "blur") {
    return {
      background: `linear-gradient(135deg, ${primary[100]}, ${neutral[50]} 45%, ${primary[300]})`,
      filter: "blur(2.5px)"
    };
  }
  // Transparent: the conventional checkerboard, so "no background" is not
  // confused with "white background".
  return {
    backgroundColor: "common.white",
    backgroundImage: `linear-gradient(45deg, ${neutral[300]} 25%, transparent 25%, transparent 75%, ${neutral[300]} 75%), linear-gradient(45deg, ${neutral[300]} 25%, transparent 25%, transparent 75%, ${neutral[300]} 75%)`,
    backgroundSize: "8px 8px",
    backgroundPosition: "0 0, 4px 4px"
  };
};

interface ScreenshotStylePickerProps {
  backgroundId: ScreenshotBackgroundId;
  paddingId: ScreenshotPaddingId;
  onBackgroundChange: (id: ScreenshotBackgroundId) => void;
  onPaddingChange: (id: ScreenshotPaddingId) => void;
  disabled?: boolean;
}

/**
 * Backdrop and margin controls for the screenshot dialog.
 *
 * `ToggleButtonGroup` rather than a row of Chips: a filled Chip in this app is
 * flattened to one lilac by `app/themes/components/chip.ts` regardless of its
 * `color` prop, so a chip-based picker would render seven identical swatch
 * holders with no selection feedback. ToggleButton is the component MUI
 * intends for this and carries `aria-pressed` on its own.
 */
export default function ScreenshotStylePicker({
  backgroundId,
  paddingId,
  onBackgroundChange,
  onPaddingChange,
  disabled
}: ScreenshotStylePickerProps) {
  const selected = findBackground(backgroundId);

  return (
    <Stack gap={2}>
      <Stack gap={0.75}>
        <Stack direction="row" alignItems="baseline" gap={1}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Background
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {selected.label}
          </Typography>
        </Stack>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={backgroundId}
          disabled={disabled}
          // A group that can be emptied would leave the preview with no
          // backdrop to draw, so a click on the active swatch is a no-op.
          onChange={(_, value) => {
            if (value) onBackgroundChange(value as ScreenshotBackgroundId);
          }}
          aria-label="Screenshot background"
          data-testid="screenshot-background-picker"
          sx={{ flexWrap: "wrap", gap: 1, border: "none" }}
        >
          {SCREENSHOT_BACKGROUNDS.map((background) => (
            <ToggleButton
              key={background.id}
              value={background.id}
              aria-label={background.label}
              data-testid={`screenshot-background-${background.id}`}
              sx={{
                p: 0.5,
                borderRadius: 1.5,
                // Every state keeps the same box: a selected swatch that grew
                // would reflow the row under the pointer mid-comparison.
                border: `2px solid ${neutral[200]}`,
                "&.Mui-selected": {
                  borderColor: primary[400],
                  backgroundColor: "transparent"
                },
                "&.Mui-selected:hover": { backgroundColor: "transparent" },
                "&.MuiToggleButtonGroup-grouped": {
                  borderRadius: 1.5,
                  ml: 0
                }
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 40,
                  height: 28,
                  borderRadius: 1,
                  overflow: "hidden",
                  ...swatchFill(background)
                }}
              />
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Stack gap={0.75}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          Margin
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={paddingId}
          disabled={disabled}
          onChange={(_, value) => {
            if (value) onPaddingChange(value as ScreenshotPaddingId);
          }}
          aria-label="Screenshot margin"
          data-testid="screenshot-padding-picker"
        >
          {SCREENSHOT_PADDINGS.map((padding) => (
            <ToggleButton
              key={padding.id}
              value={padding.id}
              data-testid={`screenshot-padding-${padding.id}`}
              sx={{ textTransform: "none", px: 2 }}
            >
              {padding.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>
    </Stack>
  );
}
